import * as React from 'react';

import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  Skeleton,
  Typography,
} from '@mui/material';

import styles from './OptimizationRunCard.module.css';

export interface OptimizationRunCardProps {
  reportId: string;
  title: string;
  subtitle?: string;
  selected: boolean;
  onToggle: () => void;
  versions: string[];
  selectedVersions: string[];
  onToggleVersion: (version: string) => void;
  loadingVersions?: boolean;
  error?: string;
}

/** One optimization run in the mix-and-match step, with its selectable versions. */
const OptimizationRunCard: React.FC<OptimizationRunCardProps> = ({
  reportId,
  title,
  subtitle,
  selected,
  onToggle,
  versions,
  selectedVersions,
  onToggleVersion,
  loadingVersions,
  error,
}) => {
  const headingId = `opt-run-${reportId}`;
  return (
    <Card
      variant="outlined"
      className={styles.card}
      sx={{ borderColor: error ? 'error.main' : selected ? 'primary.main' : undefined }}
      aria-labelledby={headingId}
      role="group"
    >
      <CardContent className={styles.content}>
        <FormControlLabel
          control={<Checkbox checked={selected} onChange={onToggle} />}
          label={
            <Box>
              <Typography id={headingId} variant="subtitle2">
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="caption" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
          }
        />
        {/* Outside the label so selecting the UID to copy it doesn't toggle the run. */}
        <Typography variant="caption" color="text.secondary" className={styles.reportUid}>
          Report UID <span className={styles.reportUidValue}>{reportId}</span>
        </Typography>
        {selected && (
          <Box className={styles.versions}>
            <Typography variant="caption" color="text.secondary" className={styles.versionsLabel}>
              Versions
            </Typography>
            {loadingVersions && <Skeleton width={160} height={32} aria-label="Loading versions" />}
            {!loadingVersions && versions.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No versions found for this run.
              </Typography>
            )}
            {!loadingVersions &&
              versions.map((v) => {
                const on = selectedVersions.includes(v);
                return (
                  <Chip
                    key={v}
                    label={`v${v}`}
                    role="checkbox"
                    aria-checked={on}
                    aria-label={`Version ${v}`}
                    color={on ? 'primary' : 'default'}
                    variant={on ? 'filled' : 'outlined'}
                    onClick={() => onToggleVersion(v)}
                  />
                );
              })}
          </Box>
        )}
        {error && (
          <Typography variant="caption" color="error" role="alert">
            {error}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default OptimizationRunCard;
